import { Dispatch, FC, useMemo, useState, useEffect } from 'react';
import { trpc, Customer } from 'src/trpc';
import { Autocomplete, AutocompleteItem } from 'src/forms/Autocomplete';
import { Loader } from 'src/components/Loader';

interface Props {
  setCustomer: Dispatch<Customer>;
  selectedCustomer?: Customer;
  className?: string;
  userId?: string;
  isLoading?: boolean;
  limit?: number;
}

export const AutocompleteUser: FC<Props> = ({
  setCustomer,
  selectedCustomer,
  className,
  userId,
  isLoading,
  limit = 100,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch customers with server-side search
  const {
    data,
    isLoading: isLoadingCustomers,
    refetch: refetchCustomers,
  } = trpc.admin.customer.adminListCustomers.useQuery({
    search: debouncedSearchTerm,
    limit,
  });

  // Transform user data to AutocompleteItem format
  const autocompleteItems: AutocompleteItem[] = useMemo(() => {
    return (data?.customers || []).map((customer: Customer) => ({
      id: customer._id || customer.friendlyId || '',
      name: `${customer.name?.first || ''} ${customer.name?.last || ''}`.trim() || 'Unknown',
      email: customer.email || '',
      friendlyId: customer.friendlyId?.toString(),
      type: customer.type as 'individual' | 'contractor' | undefined,
      userId: customer.userId,
    }));
  }, [data?.customers]);

  // Transform selected customer to AutocompleteItem format
  const selectedItem: AutocompleteItem | undefined = useMemo(() => {
    if (userId) {
      return autocompleteItems.find((item) => item.userId === userId);
    }
    if (!selectedCustomer) return undefined;

    return {
      id: selectedCustomer._id || selectedCustomer.friendlyId || '',
      name:
        `${selectedCustomer.name?.first || ''} ${selectedCustomer.name?.last || ''}`.trim() ||
        'Unknown',
      email: selectedCustomer.email || '',
      friendlyId: selectedCustomer.friendlyId?.toString(),
      type: selectedCustomer.type as 'individual' | 'contractor' | undefined,
    };
  }, [selectedCustomer, autocompleteItems, userId]);

  const handleSelect = (item: AutocompleteItem) => {
    const customer = data?.customers?.find(
      (d: Customer) => (d._id || d.friendlyId || '') === item.id,
    );
    if (customer) {
      setCustomer(customer);
    }
  };

  if (isLoadingCustomers) {
    return (
      <div className={`flex items-center justify-center ${className} py-12`}>
        <Loader title="Loading customers..." />
      </div>
    );
  }

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleRefetch = () => {
    refetchCustomers();
  };

  return (
    <Autocomplete
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={handleSelect}
      searchPlaceholder="Search by name, email, or ID..."
      className={className}
      isLoading={isLoading || isLoadingCustomers}
      refetchCustomers={handleRefetch}
      onSearch={handleSearch}
      searchValue={searchTerm}
    />
  );
};
